<script lang="ts" setup>
import { provide, ref, computed, toRaw, onMounted } from 'vue'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

import type { InvestmentProject } from '@/server/api/buyer-front/types'

import AppStaticHeader from '@/components/App/StaticHeader/index.vue'
import PdAmenities from '@/components/pd/Amenities/index.vue'
import LockOverlay from '@/components/LockOverlay/index.vue'
import PdHero from '@/components/pd/Hero/index.vue'
import PdInvestSafe from '@/components/pd/PdInvestSafe/index.vue'
import PdBuilderInfo from '@/components/pd/PdBuilderInfo/index.vue'
import PdReasonProject from '@/components/pd/PdReasonProject/index.vue'
import PdMultimediaContent from '@/components/pd/PdMultimediaContent/index.vue'
import PdFixedCtas from '@/components/pd/PdFixedCtas/index.vue'
import QuotationPlanTypologies from '@/components/QuotationPlan/Typologies/index.vue'
import PdRecommendations from '@/components/pd/Recommendations/index.vue'
import PdInvestmentPropertyDetails from '@/components/pd/PdInvestmentPropertyDetails/index.vue'
import { useAppChatStore } from '@/stores/appChatStore'
import { useUserStore } from '@/stores/userStore'
import ReferralBannerHandler from '@/components/Referrals/BannerHandler/index.vue'
import PdInPersonVisit from '@/components/pd/InPersonVisit/index.vue'
import { getIsInPersonVisitActive } from '@/utils/project'
import AppMapStatic from '@/components/App/Map/Static/index.vue'

import { LazyAppModal } from '#components'

import { investmentProjectKey } from '@/symbols/listing'

import { SCREEN } from '@/utils/segment'

const route = useRoute()
const { $sobreplanosDataService, $trackEvent, $config } = useNuxtApp()

const { t, tm, rt } = useI18n()

const appChatStore = useAppChatStore()
const userStore = useUserStore()

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.greater('sm')

const optionalParams = {
  refresh: route.query.refresh === 'true'
}

const slug = route.params.slug as string
const isDemo = route.query.mode === 'demo'
const { data: mainDataRef, error } = await useAsyncData<InvestmentProject>('mainDataRef', () => $sobreplanosDataService.getListing(slug, optionalParams.refresh, isDemo))

if (error.value || !mainDataRef.value?.code) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not found',
    fatal: true
  })
}

const listing = toRaw(mainDataRef.value!)
const coverImage = computed(() => listing.cover ? `${listing.cover}?auto=compress,enhance,format` : 'https://lahaus.imgix.net/static/default/default-share-social-media-v2.jpg')

const isInPersonVisitActive = computed(() => {
  const listingId = listing.code
  const businessHubCode = listing.raw_listing?.location?.business_hub_code
  return getIsInPersonVisitActive(listingId, businessHubCode, $config)
})

const appChat = ref()

useSeoMeta({
  robots: 'index, follow',
  title: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood}`,
  ogTitle: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood}`,
  description: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood} ¡invierte en bienes raíces y benefíciate de la renta hotelera y turística!`,
  ogDescription: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood} ¡invierte en bienes raíces y benefíciate de la renta hotelera y turística!`,
  ogImage: coverImage,
  ogSiteName: 'La Haus',
  twitterCard: 'summary'
})

provide(investmentProjectKey, listing)

const projectLatitude = computed(() => listing.raw_listing?.location?.latitude)
const projectLongitude = computed(() => listing.raw_listing?.location?.longitude)
const projectLocationName = computed(() => `${listing.state_label}, ${listing.neighborhood_label}`)

const inventoryPath = `/planes-de-pago/${listing.raw_listing?.slugs[0]}`
const handleCreatePaymentPlan = () => {
  return navigateTo({
    path: inventoryPath
  })
}

const videos = computed(() => {
  const list = listing.raw_listing?.attributes?.videos
  if (!list || list?.length === 0) return undefined
  else return list
})

const tours = computed(() => {
  return listing.raw_listing?.attributes?.tour
})

const webinars = computed(() => {
  return listing.ims?.webinars_urls
})

const isMediaContentAvailable = computed(() => {
  return !!videos.value || !!tours.value || !!webinars.value
})

const canShowQuotationPlans = computed(() => !!listing.ims?.typologies)

const getImages = (imageType:string) => {
  if (!listing.pictures_v2) return []

  return listing.pictures_v2
    .filter(image => image.type === imageType)
    .sort((a, b) => a.order - b.order)
    .map(image => image.url)
}

const allImages = computed(() => {
  const gallery = getImages('gallery')
  const blueprints = getImages('blueprints')

  return [
    listing.cover,
    ...gallery,
    ...blueprints
  ]
})

const showGallery = ref(false)

const handleOpenGallery = (tabName: string) => {
  currentTab.value = tabName
  showGallery.value = true
}

const handleCloseGallery = () => {
  showGallery.value = false
}

const locationParams = {
  lat: listing.raw_listing?.location?.latitude,
  lng: listing.raw_listing?.location?.longitude,
  mapId: `dynamic-google-map-${listing.code}`
}

const isDesktop = breakpoints.greater('md')

const currentTab = ref('pictures')

const opacityValue = ref(false)

const isBottomCtaVisible = ref(true)

const toggleOpacity = (value: boolean) => {
  opacityValue.value = value
}

const goToForm = () => {
  const offset = 190
  const fallbackSelector = '.quotation-plan-fee-simulator'

  const fallbackElement = document.querySelector(fallbackSelector) as HTMLElement
  const elementPaymentPlan = document.querySelector('#pd-quotation-plan-table') as HTMLElement
  const element = elementPaymentPlan || fallbackElement

  if (!element) return

  const y = element.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top: y, behavior: 'smooth' })
}

const isContentLocked = computed(() => {
  const referrerId = route.query.referrer_id as string
  return !userStore.isAuthenticated && !referrerId
})

const handleScheduleButton = () => {
  const endpointConfig = {
    screen: 'PDP',
    entryNameAlias: 'PDP_CHAT_WEB_SCHEDULE',
    startFlow: true,
    shouldCallBotForGreeting: true
  }
  appChatStore.openChatWindow(endpointConfig)
}

onMounted(() => {
  appChatStore.updateMessagesChips(tm('samGpt.chipsPdp')?.map(chip => ({
    id: rt(chip.id),
    title: rt(chip.title),
    message: rt(chip.message),
    type: rt(chip.type)
  })))

  const cookieName = `listingCollection_last_card_${listing.code}`
  const collectionCookie = useCookie(cookieName)
  const cookieData = collectionCookie.value

  $trackEvent({
    name: 'listing_viewed',
    version: '5-0-0',
    data: {
      listings: [
        {
          id: listing.code,
          source: 'PROPERTY_CATALOG'
        }
      ],
      url: window.location.href,
      view_platform: 'WEB',
      collection_id: cookieData?.collection_id || '',
      collection_position: cookieData?.index || 0
    }
  })

  collectionCookie.value = null
})
</script>

<script lang="ts">
export default {
  name: 'PagePdCitySlugV1'
}
</script>

<template>
  <main
    class="page-pd-city-slug h-fit"
    :class="{ 'overflow-hidden': isContentLocked }">
    <AppStaticHeader class="w-full fixed top-0" />

    <LockOverlay
      v-if="isContentLocked"
      :screen="SCREEN.PDP_INVESTORS"
      :listing-id="listing.code"
      :should-enqueue-twilio-contact="listing.ims?.external_status === 'active'"
      class="z-40 fixed h-screen bottom-0" />

    <PdHero
      class="with-tag"
      :listing="listing"
      :show-features-summary="true"
      @open-gallery="handleOpenGallery" />

    <div class="grid grid-cols-12 max-w-[1600px] mx-auto">
      <div
        class="col-span-12">
        <QuotationPlanTypologies
          v-if="canShowQuotationPlans"
          id="pd-unit-cards"
          :show-blueprint="false"
          class="page-pd-city-slug__section page-pd-city-slug__section--padded"
          :listing-id="listing.code"
          :listing-name="listing.name"
          :listing-type="listing.raw_listing.attributes.product_type"
          :typologies="listing.ims.typologies">
          <template #contact-form>
            <div
              id="pd-schedule-visit-section"
              class="pt-16 xl:pt-32">
              <h3 class="text-28 text-carbon-800 font-semibold">
                {{ t('pd.scheduleSection.title') }}
              </h3>

              <p>
                {{ t('pd.scheduleSection.subtitle') }}
              </p>

              <RoomieButton
                id="pd-schedule-visit-cta"
                class="mt-24 w-full"
                variant="outlined"
                data-lh-id="pd-schedule-visit-cta"
                @click="handleScheduleButton">
                <div class="flex items-center gap-8 justify-center">
                  {{ t('pd.scheduleSection.cta') }}
                </div>
              </RoomieButton>
            </div>
          </template>
        </QuotationPlanTypologies>

        <div>
          <PdInPersonVisit
            v-if="isInPersonVisitActive"
            :listing="listing"
            :lat="projectLatitude"
            :lng="projectLongitude"
            :location-name="projectLocationName"
            :is-mobile="isMobile"
            :open-gallery="handleOpenGallery" />
        </div>
      </div>

      <div class="col-span-12">
        <section class="page-pd-city-slug__section grid gap-24 xl:px-56">
          <section
            v-if="!isInPersonVisitActive"
            id="pd-map-section"
            class="md:px-24 pt-32 pb-24">
            <AppMapStatic
              :id="`pdp-static-map-button-${ listing.code }`"
              class="flex-1 is-form-visible"
              :lat="projectLatitude"
              :lng="projectLongitude"
              :location-name="projectLocationName"
              :is-mobile="isMobile"
              button-styles="min-h-[24.4rem] md:min-h-max md:h-[calc(100%-60px)] lg:min-h-[20rem] lg:h-[calc(100%-76px)]"
              img-styles="h-full lg:h-full"
              @open-gallery="handleOpenGallery" />
          </section>

          <PdMultimediaContent
            v-if="isMediaContentAvailable"
            class="px-24 md:px-0"
            :listing="listing"
            :go-to-form
            @create-payment-plan="handleCreatePaymentPlan"
            @open-gallery="handleOpenGallery" />
        </section>

        <div class="page-pd-city-slug__section page-pd-city-slug__section--padded w-full mb-lh-12 py-24">
          <ReferralBannerHandler
            ab-testing-key="pdp-referral-banner"
            source-page="pdp" />
        </div>

        <PdInvestmentPropertyDetails
          :listing="listing"
          class="page-pd-city-slug__section page-pd-city-slug__section--padded" />

        <div class="page-pd-city-slug__section md:px-24 xl:px-56">
          <PdReasonProject class="mt-24" />

          <PdAmenities />

          <PdBuilderInfo
            class="md:mt-56"
            :builder-info="listing.builder_info"
            :project-city="listing.city_label" />

          <PdInvestSafe class="md:mt-56" />
        </div>

        <NuxtLazyHydrate when-visible>
          <PdRecommendations
            :project-code="listing.code"
            :business-hub="listing.raw_listing?.location.business_hub"
            :refresh="optionalParams.refresh"
            class="page-pd-city-slug__section" />
        </NuxtLazyHydrate>

        <div
          v-if="isDemo"
          class="demo-banner"
          style="background-color: #FFCE09; z-index: 9999;">
          <span
            class="block w-full h-10"
            style="background: repeating-linear-gradient(45deg,#FFCE09, #FFCE09 10px, #000 10px, #000 20px);" />
          DEMO
          <span
            class="block w-full h-10"
            style="background: repeating-linear-gradient(45deg,#FFCE09, #FFCE09 10px, #000 10px, #000 20px);" />
        </div>
      </div>

      <PdFixedCtas
        class="hidden-whatsapp z-30"
        :listing
        :observable-selectors="['.quotation-plan-typologies .typology-container', '.pd-sam-contact-form']"
        @click-personalize-plan-cta="goToForm"
        @show-cta="(isVisible) => isBottomCtaVisible = isVisible" />

      <LazyAppModal
        v-if="showGallery"
        class="gallery-modal"
        :class="{'opacity-modal' : opacityValue}"
        @close="handleCloseGallery">
        <LazyAppGallery
          id="pdp-hero"
          :pictures="allImages"
          :videos="videos"
          :webinars="webinars"
          :tours="tours"
          :current-tab="currentTab"
          :is-mobile="!isDesktop"
          :location="locationParams"
          :handle-close-gallery
          @opacity-event="toggleOpacity" />
      </LazyAppModal>
    </div>

    <div
      v-if="!isContentLocked"
      class="transition-all duration-500 ease-in-out fixed right-[2rem] md:right-[5.6rem] bottom-[14rem] md:bottom-[8rem] z-60"
      :class="{ '!bottom-[2rem]': !isBottomCtaVisible}">
      <AppChat
        ref="appChat"
        bot-agent-name="Sam"
        bot-agent-image="https://lahaus.imgix.net/static/lh-landing/img-sam-profile.png?auto=compress,enhance,format"
        :listing
        selector-to-trigger-button-chips=".investment-summary-new-version"
        :hiders-selectors="['.sam-contact-form__form-container .roomie-phone-input']"
        :face-to-face-visit-enabled="true" />
    </div>
  </main>
</template>

<style lang="scss">
.page-pd-city-slug {
  &__section {
    @apply mx-auto;

    &--padded {
      @apply px-24 xl:px-56;
    }
  }

  .map-container {
    @apply px-24 md:px-0;
  }

  .pd-unit-cards {
    @apply -mx-24 mt-24;
  }

  .pd-contact-form {
    @media(width >= 1024px){
      max-width: 564px;
    }
  }

  .recommendations-carousel {
    @apply lg:px-56 mx-auto;

    max-width: 1168px;

    & h2 {
      @apply px-24 lg:px-0;
    }

    &__holder {
      @apply mx-auto max-w-screen-xl;
    }
  }

  .gallery-modal {
    & .roomie-modal {
      @apply max-w-[100rem];

      &__body {
        @apply h-full;
      }

      &__content {
        height: calc(100% - 32px)
      }

      &__button {
        @apply z-10;
      }
    }
  }

  & .opacity-modal {
    & .roomie-modal {
      @apply opacity-0 duration-200;
    }
  }

  .contact-form-whatsapp-float {
    width: 48px;
    height: 48px !important;
  }

  .is-form-visible {
    img {
      @apply lg:rounded-sm;
    }

    p {
      @apply bg-brand-50 text-brand-800 border border-brand-800 w-176 z-10 right-[1.6rem] left-[1.6rem];
    }
  }

  .hidden-whatsapp {
    .pd-floating-button-whatsapp-contact {
      display: none;
    }
  }

  .roomie-tooltip {
    z-index: 1;
  }

  .demo-banner {
    @apply fixed bottom-0 left-0 w-full text-center py-4 font-semibold;
  }

  .pd-sam-contact-form {
    box-shadow: 0 0 20px 0 rgba(0 0 0 / 10%);
  }

  .roomie-footer {
    @apply md:pb-0;

    padding-bottom: 60px;
  }
}
</style>
